import axios from 'axios';
import authService from './authService';
import { RUNTIME_CONFIG } from '../config/runtime';

const API_BASE_URL = RUNTIME_CONFIG.API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track in-flight requests so the UI can show a global progress indicator.
// The UIProvider listens for these events and bumps a counter.
const notifyProgress = (delta: 'start' | 'stop') => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(`wardrobe:progress-${delta}`));
};

api.interceptors.request.use((config) => {
  notifyProgress('start');
  return config;
});

api.interceptors.response.use(
  (response) => {
    notifyProgress('stop');
    return response;
  },
  (error) => {
    notifyProgress('stop');
    return Promise.reject(error);
  }
);

// Request interceptor to add authentication token
api.interceptors.request.use(
  (config) => {
    const token = authService.getAccessToken();
    console.log('🔑 API Request:', {
      url: config.url,
      method: config.method,
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : null
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      url: response.config.url,
      status: response.status
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.log('❌ API Response Error:', {
      url: originalRequest?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      isRetry: originalRequest?._retry
    });

    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔄 Token expired, attempting refresh...');
      originalRequest._retry = true;

      try {
        await authService.refreshAccessToken();
        const token = authService.getAccessToken();
        if (token) {
          console.log('✅ Token refreshed successfully, retrying request');
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } else {
          console.error('❌ Token refresh returned no token');
          throw new Error('No token after refresh');
        }
      } catch (refreshError: any) {
        // Check if it's a rate limit error
        if (refreshError.message === 'RATE_LIMITED') {
          console.warn('⚠️ Token refresh rate limited, but token may still be valid');
          // Don't logout immediately - let the user try again
          return Promise.reject(error);
        }

        // Refresh failed, redirect to login
        console.error('❌ Token refresh FAILED, logging out:', refreshError);
        authService.logout();
        window.location.href = '/login?reason=session_expired';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Wardrobe Items
export const wardrobeAPI = {
  getItems: () =>
    api.get('/wardrobe/items'),

  getItem: (itemId: number) =>
    api.get(`/wardrobe/items/${itemId}`),

  createItem: (data: any) =>
    api.post('/wardrobe/items', data),

  updateItem: (itemId: number, data: any) =>
    api.put(`/wardrobe/items/${itemId}`, data),

  deleteItem: (itemId: number) =>
    api.delete(`/wardrobe/items/${itemId}`),

  recordWear: (itemId: number) =>
    api.post(`/wardrobe/items/${itemId}/wear`),

  markWorn: (data: { item_ids: number[]; date?: string; image_url?: string; image_s3_key?: string }) =>
    api.post('/wardrobe/items/mark-worn', data),

  getPlannedFit: (date?: string) =>
    api.get('/wardrobe/planned-fit', { params: date ? { date } : undefined }),

  getFitHistory: (limit?: number) =>
    api.get('/wardrobe/fit-history', { params: limit ? { limit } : undefined }),

  getItemUsage: (itemId: number) =>
    api.get(`/wardrobe/items/${itemId}/usage`),

  searchItems: (query: any) =>
    api.post('/wardrobe/search', query),

  // Multi-image management
  addImage: (itemId: number, formData: FormData) =>
    api.post(`/wardrobe/items/${itemId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  deleteImage: (itemId: number, imageId: string) =>
    api.delete(`/wardrobe/items/${itemId}/images/${imageId}`),

  setPrimaryImage: (itemId: number, imageId: string) =>
    api.put(`/wardrobe/items/${itemId}/images/${imageId}/set-primary`),

  reorderImages: (itemId: number, imageOrder: string[]) =>
    api.put(`/wardrobe/items/${itemId}/images/reorder`, { image_order: imageOrder }),

  // Export / Import
  exportWardrobe: () =>
    api.get('/wardrobe/export'),

  getImportUploadUrl: () =>
    api.post('/wardrobe/import/upload-url'),

  importWardrobe: (s3Key: string) =>
    api.post('/wardrobe/import', { s3_key: s3Key }),
};

// Upload a file directly to a presigned S3 URL.
// Uses fetch (not the `api`/global axios instances) so no Authorization header
// is attached - the presigned URL carries its own credentials in the query string.
export const uploadToPresignedUrl = async (uploadUrl: string, file: File | Blob) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': 'application/zip' },
  });
  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
};

// Trigger a browser download for a (presigned) URL.
export const triggerDownload = (url: string, filename?: string) => {
  const link = document.createElement('a');
  link.href = url;
  if (filename) link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Outfits
export const outfitsAPI = {
  getOutfits: () =>
    api.get('/outfits'),

  getOutfit: (outfitId: number) =>
    api.get(`/outfits/${outfitId}`),

  createOutfit: (data: any) =>
    api.post('/outfits', data),

  updateOutfit: (outfitId: number, data: any) =>
    api.put(`/outfits/${outfitId}`, data),

  deleteOutfit: (outfitId: number) =>
    api.delete(`/outfits/${outfitId}`),

  recordWear: (outfitId: number) =>
    api.post(`/outfits/${outfitId}/wear`),

  recommendOutfits: (data: any) =>
    api.post('/outfits/recommend', data),

  getOccasions: () =>
    api.get('/outfits/occasions'),
};

// Upload
export const uploadAPI = {
  analyzeImage: (formData: FormData) =>
    api.post('/upload/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Fit of the Day: detect worn items in a selfie and match them to the wardrobe
  analyzeFit: (formData: FormData) =>
    api.post('/upload/analyze-fit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Render the user wearing an outfit (Nova Canvas virtual try-on)
  visualizeOutfit: (data: { outfit_id?: number; item_ids?: number[] }) =>
    api.post('/upload/visualize-outfit', data),

  analyzePerson: (data: { s3_key: string; image_url: string; person_description?: string; bounding_box?: any }) =>
    api.post('/upload/analyze-person', data),

  createItemFromUpload: (data: any) =>
    api.post('/upload/create-item', data),

  createItemsBatch: (data: { items: any[] }) =>
    api.post('/upload/create-items-batch', data),

  analyzeCrop: (data: { image_data: string; s3_key?: string; image_url?: string }) =>
    api.post('/upload/analyze-crop', data),

  detectPeople: (formData: FormData) =>
    api.post('/upload/detect-people', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  cropPerson: (data: { s3_key: string; bounding_box: any }) =>
    api.post('/upload/crop-person', data),

  checkDuplicates: (data: { items: any[] }) =>
    api.post('/upload/check-duplicates', data),
};

// Collections
export const collectionsAPI = {
  getCollections: () =>
    api.get('/collections'),

  getCollection: (collectionId: number) =>
    api.get(`/collections/${collectionId}`),

  createCollection: (data: any) =>
    api.post('/collections', data),

  updateCollection: (collectionId: number, data: any) =>
    api.put(`/collections/${collectionId}`, data),

  deleteCollection: (collectionId: number) =>
    api.delete(`/collections/${collectionId}`),

  addOutfitToCollection: (collectionId: number, outfitId: number) =>
    api.post(`/collections/${collectionId}/outfits`, { outfit_id: outfitId }),

  removeOutfitFromCollection: (collectionId: number, outfitId: number) =>
    api.delete(`/collections/${collectionId}/outfits/${outfitId}`),
};

// Look Book: inspiration looks matched against the wardrobe
export const looksAPI = {
  getLooks: () =>
    api.get('/looks'),

  getLook: (lookId: number) =>
    api.get(`/looks/${lookId}`),

  createLook: (data: any) =>
    api.post('/looks', data),

  updateLook: (lookId: number, data: any) =>
    api.put(`/looks/${lookId}`, data),

  deleteLook: (lookId: number) =>
    api.delete(`/looks/${lookId}`),

  // One photo per request: vision analysis + wardrobe matching for that photo
  addImage: (lookId: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/looks/${lookId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteImage: (lookId: number, imageId: string) =>
    api.delete(`/looks/${lookId}/images/${imageId}`),

  // Suggest name/description/occasion/season/tags from a photo (file or saved s3_key)
  describe: (source: File | string) => {
    if (typeof source === 'string') return api.post('/looks/describe', { s3_key: source });
    const formData = new FormData();
    formData.append('image', source);
    return api.post('/looks/describe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  rematch: (lookId: number) =>
    api.post(`/looks/${lookId}/rematch`),

  saveOutfit: (lookId: number, data: { name?: string; item_ids?: number[]; outfit_id?: number }) =>
    api.post(`/looks/${lookId}/outfits`, data),

  detachOutfit: (lookId: number, outfitId: number) =>
    api.delete(`/looks/${lookId}/outfits/${outfitId}`),
};

// Calendar
export const calendarAPI = {
  getEvents: (params?: any) =>
    api.get('/calendar/events', { params }),

  getEvent: (eventId: number) =>
    api.get(`/calendar/events/${eventId}`),

  createEvent: (data: any) =>
    api.post('/calendar/events', data),

  updateEvent: (eventId: number, data: any) =>
    api.put(`/calendar/events/${eventId}`, data),

  deleteEvent: (eventId: number) =>
    api.delete(`/calendar/events/${eventId}`),

  getDailyOutfits: (params?: any) =>
    api.get('/calendar/daily', { params }),

  setDailyOutfit: (data: any) =>
    api.post('/calendar/daily', data),

  getMonthlyView: (year: number, month: number) =>
    api.get(`/calendar/monthly/${year}/${month}`),

  suggestOutfit: (data: any) =>
    api.post('/calendar/suggest', data),

  // Google Calendar Integration
  getGoogleCalendarStatus: () =>
    api.get('/calendar/google/status'),

  getGoogleAuthUrl: () =>
    api.get('/calendar/google/auth-url'),

  connectGoogleCalendar: (data: any) =>
    api.post('/calendar/google/connect', data),

  disconnectGoogleCalendar: () =>
    api.post('/calendar/google/disconnect'),

  listGoogleCalendars: () =>
    api.get('/calendar/google/calendars'),

  syncGoogleCalendar: (data: { calendar_ids?: string[]; days_ahead?: number }) =>
    api.post('/calendar/google/sync', data),

  searchEvents: (params?: any) =>
    api.get('/calendar/events/search', { params }),
};

// Trips
export const tripsAPI = {
  getTrips: () =>
    api.get('/trips'),

  getTrip: (tripId: number) =>
    api.get(`/trips/${tripId}`),

  createTrip: (data: any) =>
    api.post('/trips', data),

  updateTrip: (tripId: number, data: any) =>
    api.put(`/trips/${tripId}`, data),

  deleteTrip: (tripId: number) =>
    api.delete(`/trips/${tripId}`),

  generatePlan: (tripId: number) =>
    api.post(`/trips/${tripId}/generate-plan`),

  getPackingList: (tripId: number) =>
    api.get(`/trips/${tripId}/packing-list`),

  updatePlan: (tripId: number, data: { item_ids?: number[]; outfit_ids?: number[]; collection_ids?: number[] }) =>
    api.put(`/trips/${tripId}/plan`, data),
};

// Users
export const usersAPI = {
  // Get a user's profile (defaults to own profile if userId not provided)
  getProfile: (userId?: string) =>
    api.get('/users/profile', { params: userId ? { user_id: userId } : {} }),

  updateProfile: (data: any) =>
    api.put('/users/profile', data),

  // Full-body reference photo (used to render the user wearing outfits)
  uploadFullBodyPhoto: (formData: FormData) =>
    api.post('/users/profile/full-body-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteFullBodyPhoto: () =>
    api.delete('/users/profile/full-body-photo'),

  // Search for users (current user comes from JWT)
  searchUsers: (query: string, limit?: number) =>
    api.get('/users/search', { params: { q: query, limit } }),

  // Get privacy settings for current user (always from JWT)
  getPrivacySettings: () =>
    api.get('/users/privacy'),

  updatePrivacySettings: (data: any) =>
    api.put('/users/privacy', data),

  // Validate username uniqueness (current user comes from JWT)
  validateUsername: (username: string) =>
    api.post('/users/validate-username', { username }),

  // Get user stats (defaults to own stats if userId not provided)
  getUserStats: (userId?: string) =>
    api.get('/users/stats', { params: userId ? { user_id: userId } : {} }),
};

// Friends
export const friendsAPI = {
  getFriends: () =>
    api.get('/friends'),

  getSentRequests: () =>
    api.get('/friends/requests/sent'),

  getReceivedRequests: () =>
    api.get('/friends/requests/received'),

  sendFriendRequest: (data: any) =>
    api.post('/friends/request', data),

  acceptFriendRequest: (requestId: number, data: any) =>
    api.post(`/friends/request/${requestId}/accept`, data),

  declineFriendRequest: (requestId: number, data: any) =>
    api.post(`/friends/request/${requestId}/decline`, data),

  removeFriend: (friendId: string, data: any) =>
    api.delete(`/friends/${friendId}/remove`, { data }),

  blockUser: (friendId: string, data: any) =>
    api.post(`/friends/${friendId}/block`, data),

  getFriendPrivacy: (friendId: string) =>
    api.get(`/friends/${friendId}/privacy`),

  updateFriendPrivacy: (friendId: string, data: any) =>
    api.put(`/friends/${friendId}/privacy`, data),

  getFriendshipStatus: (friendId: string) =>
    api.get(`/friends/status/${friendId}`),
};

// Activity
export const activityAPI = {
  getActivityFeed: (params?: any) =>
    api.get('/activity/feed', { params }),

  getUserActivities: (targetUserId: string, params?: any) =>
    api.get(`/activity/user/${targetUserId}`, { params }),

  createActivity: (data: any) =>
    api.post('/activity/create', data),

  getTrendingActivities: (params?: any) =>
    api.get('/activity/trending', { params }),

  getActivityStats: (days?: number) =>
    api.get('/activity/stats', { params: { days } }),
};

// Notifications
export const notificationsAPI = {
  getNotifications: (params?: any) =>
    api.get('/notifications', { params }),

  markAsRead: (notificationId: number) =>
    api.post(`/notifications/${notificationId}/read`),

  markAllAsRead: () =>
    api.post('/notifications/mark-all-read'),

  deleteNotification: (notificationId: number) =>
    api.delete(`/notifications/${notificationId}`),
};

// Conversations API
export const conversationsAPI = {
  getConversations: (params?: any) =>
    api.get('/conversations', { params }),

  getConversation: (conversationId: string, params?: any) =>
    api.get(`/conversations/${conversationId}`, { params }),

  createConversation: (data: {
    title?: string;
    description?: string;
    conversation_type?: string;
  } = {}) =>
    api.post('/conversations', {
      conversation_type: 'wardrobe_chat',
      ...data
    }),

  updateConversation: (conversationId: string, data: any) =>
    api.put(`/conversations/${conversationId}`, data),

  deleteConversation: (conversationId: string, permanent: boolean = false) =>
    api.delete(`/conversations/${conversationId}`, {
      params: { permanent: permanent ? 'true' : 'false' }
    }),

  sendMessage: (conversationId: string, data: {
    message: string;
    include_search?: boolean;
  }) =>
    api.post(`/conversations/${conversationId}/messages`, {
      include_search: true,
      ...data
    }),

  getMessages: (conversationId: string, params?: any) =>
    api.get(`/conversations/${conversationId}/messages`, { params }),

  updateMessage: (conversationId: string, messageId: string, data: any) =>
    api.put(`/conversations/${conversationId}/messages/${messageId}`, data),

  deleteMessage: (conversationId: string, messageId: string) =>
    api.delete(`/conversations/${conversationId}/messages/${messageId}`),

  getConversationSummary: (conversationId: string) =>
    api.get(`/conversations/${conversationId}/summary`),

  getConversationMemory: (conversationId: string, params?: any) =>
    api.get(`/conversations/${conversationId}/memory`, { params }),

  createMemoryEntry: (conversationId: string, data: any) =>
    api.post(`/conversations/${conversationId}/memory`, data),

  getGlobalMemory: (params?: any) =>
    api.get('/conversations/memory/global', { params }),

  getMemoryStats: () =>
    api.get('/conversations/memory/stats'),

  getConversationSuggestions: (limit?: number) =>
    api.get('/conversations/suggestions', { params: { limit } }),

  searchConversations: (data: {
    query: string;
    limit?: number;
  }) =>
    api.post('/conversations/search', {
      limit: 20,
      ...data
    }),
};

// Weather API
export const weatherAPI = {
  getCurrentWeather: (location?: string) => 
    api.get('/weather/current', { params: { location } }),
  
  getWeatherForecast: (location?: string, days?: number) => 
    api.get('/weather/forecast', { params: { location, days: days || 7 } }),
  
  getOutfitRecommendations: (weather: any) => 
    api.post('/weather/outfit-recommendations', { weather }),
};

// Semantic Search API
export const searchAPI = {
  semanticSearch: (data: {
    query: string;
    search_type?: string;
    include_outfits?: boolean;
    limit?: number;
  }) =>
    api.post('/search/semantic', {
      search_type: 'hybrid',
      include_outfits: true,
      limit: 20,
      ...data
    }),

  analyzeQuery: (query: string) =>
    api.post('/search/analyze', { query }),

  getSearchSuggestions: (query?: string, limit?: number) =>
    api.post('/search/suggestions', {
      query: query || '',
      limit: limit || 10
    }),

  getSearchHistory: (limit?: number) =>
    api.get('/search/history', {
      params: { limit: limit || 20 }
    }),

  generateEmbeddings: (data: {
    target_type?: string;
    limit?: number;
  } = {}) =>
    api.post('/search/embeddings/generate', {
      target_type: 'both',
      limit: 100,
      ...data
    }),

  getEmbeddingStatus: () =>
    api.get('/search/embeddings/status'),

  getSmartRecommendations: (data: {
    context?: any;
    constraints?: any;
    type?: string;
  } = {}) =>
    api.post('/search/recommendations/smart', {
      type: 'outfit',
      context: {},
      constraints: {},
      ...data
    }),
};

export default api;